/* eslint-disable no-unused-expressions */
import React, { useEffect, useState } from 'react';
import { useGlobal } from '@rcg/ui-react';
import { navigate } from '@gatsbyjs/reach-router/';
import { Typography } from '@rcg/ui-react/lib/components';
import { globalizationPath } from '../../../../utils/globalization/globalizationPath';
import Button from '../../../../components/UiComponents/Button';
import AirLoader from '../../../../components/AirLoader';
import useCancelFlight from '../../../../operations/dataAccess/useCancelFlight';
import useGuestAirProfile from '../../../../operations/dataAccess/useGuestAirProfile';
import Dictionary from '../../../../utils/dictionary';
import useDictionary from '../../../../hooks/useDictionary';
import MuiModal from '../MuiModal';
import useStyles from '../../index.styles';
import {
  SESSION_STORAGE_CANCEL_FLIGHT_RESPONSE,
  NAVIGATION_PATHS,
  CANCEL_FLIGHT_RESPONSE_SUCCESSFUL,
  SESSION_STORAGE_BOOKING_PROFILE_NAME,
} from '../../../../utils/constants';
import { setSessionStorageItem } from '../../../../operations/sessionStorage/utils';
import { trackInteraction } from '../../../../utils/analytics/browseTracking';

const MAX_RETRY = 5;
const TIMEOUT = 5000;
const CancellationModal = ({
  modalOpen,
  handleCloseModal,
  currentBookingId,
  cancellationGuests,
  setNotification,
}) => {
  const classes = useStyles();
  const getDictionaryValue = useDictionary(Dictionary);
  const {
    data: { lang, market },
  } = useGlobal();
  const [gapLoader, setGapLoader] = useState(false);
  const [loaderCancellation, setLoaderCancellation] = useState(false);
  const [, setMutationCancellationState] = useState(true);
  const urlPath = globalizationPath(market, lang, NAVIGATION_PATHS.base);
  // eslint-disable-next-line
  const guests = cancellationGuests.map(
    ({ guestId, airChargesAmount, airPenaltyAmount, ...rest }) => ({
      guestID: guestId,
      ...rest,
    }),
  );

  const assigningErrorMessage = () => {
    handleCloseModal();
    setNotification({
      title: getDictionaryValue(Dictionary.airFlightsCancellationModalErrorEncounteredTitle.key),
      description: getDictionaryValue(Dictionary.airFlightCancellationBannerErrorHasOcurred.key),
      type: 'error',
      open: true,
    });
  };

  const handleCancelFlightResponse = (cancellationSuccess) => {
    if (cancellationSuccess) {
      setSessionStorageItem(SESSION_STORAGE_CANCEL_FLIGHT_RESPONSE, true);
      navigate(`${urlPath}${NAVIGATION_PATHS.cancelConfirmation}`);
    } else {
      setMutationCancellationState(false);
      const trakingParams = {
        eventType: 'interaction',
        interaction: 'cancelflight:error',
      };
      trackInteraction(trakingParams);
      setSessionStorageItem(SESSION_STORAGE_CANCEL_FLIGHT_RESPONSE, false);
      assigningErrorMessage();
    }
  };

  const [guestAirProfile] = useGuestAirProfile({ fetchPolicy: 'network-only' });

  const callGuestAirProfile = async () => {
    const bookingInfo = sessionStorage.getItem(SESSION_STORAGE_BOOKING_PROFILE_NAME);
    const bookingParse = JSON.parse(bookingInfo);
    const guestIds = guests.map((guest) => guest.guestID);
    const bookingId = bookingParse?.currentBooking?.id;

    const resp = await guestAirProfile({
      variables: {
        input: {
          bookingId,
          guestIds,
          correlationID: 'cancel',
        },
      },
    });

    return resp?.data?.guestAirProfile;
  };

  const invokeGapService = async (retry = 1) => {
    setGapLoader(true);
    try {
      if (retry > MAX_RETRY) {
        setGapLoader(false);
        handleCancelFlightResponse(false);
        return;
      }

      // Only the first time will wait
      retry === 1 && (await new Promise((resolve) => setTimeout(resolve, TIMEOUT)));

      const guestProfile = await callGuestAirProfile();

      if (!guestProfile?.hasFlights) {
        setGapLoader(false);
        handleCancelFlightResponse(true);
        return;
      }

      // if guest still has flights it will retry until getting to MAX_RETRY or hasFlights is false
      invokeGapService(retry + 1);
    } catch (error) {
      handleCancelFlightResponse(false);
      setGapLoader(false);
    }
  };

  const [cancelFlight, { loading: cancellationLoading }] = useCancelFlight({
    onCompleted: ({ cancelFlight: mutationResponse }) => {
      if (mutationResponse === CANCEL_FLIGHT_RESPONSE_SUCCESSFUL) {
        handleCancelFlightResponse(true);
      } else {
        setGapLoader(true);
        invokeGapService();
      }
    },
    onError: () => {
      setGapLoader(true);
      invokeGapService();
    },
  });

  useEffect(() => {
    if (cancellationLoading || gapLoader) {
      setLoaderCancellation(true);
    } else {
      setLoaderCancellation(false);
    }
  }, [cancellationLoading, gapLoader]);

  const handleCancelFlight = () => {
    cancelFlight({
      variables: {
        input: {
          bookingData: {
            bookingId: currentBookingId,
            guest: guests,
          },
        },
      },
    });
  };

  return (
    <>
      <AirLoader open={loaderCancellation} />
      <MuiModal
        customClass={{ width: '40%' }}
        parentCustomClass={{ zIndex: '2' }}
        modalStatus={modalOpen}
        handleClose={handleCloseModal}
        title={getDictionaryValue(Dictionary.airModalCancellationTitle.key)}>
        <div>
          <div className={classes.headerModal}>
            <Typography variant="h5" className={classes.flightsCancellationTitle}>
              {getDictionaryValue(Dictionary.airModalCancellationTitle.key)}
            </Typography>
          </div>
          <div className={classes.footerModal}>
            <Button
              data-testid="cancel-flight-no-btn"
              variant="outlined"
              color="primary"
              className={classes.buttonModal}
              onClick={handleCloseModal}>
              {getDictionaryValue(Dictionary.airModalCancellationButtonNo.key)}
            </Button>
            <Button
              data-testid="cancel-flight-yes-btn"
              variant="contained"
              color="primary"
              onClick={handleCancelFlight}>
              {getDictionaryValue(Dictionary.airModalCancellationButtonYes.key)}
            </Button>
          </div>
        </div>
      </MuiModal>
    </>
  );
};

export default CancellationModal;
